import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';

const SearchIntegration = ({ 
  onSearch = () => {},
  onFilterChange = () => {},
  recentSearches = [],
  popularCuisines = [],
  searchResults = []
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    cuisine: [],
    dietary: [],
    priceRange: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef(null);

  const cuisineOptions = [
    'Indian', 'Chinese', 'Italian', 'Mexican', 'Thai', 'Japanese', 'Mediterranean', 'American', 'Cake', 'Sweets'
  ];

  const dietaryOptions = [
    { value: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
    { value: 'vegan', label: 'Vegan', icon: 'Sprout' },
    { value: 'gluten-free', label: 'Gluten-Free', icon: 'Wheat' },
    { value: 'halal', label: 'Halal', icon: 'Moon' }
  ];

  const priceRanges = [
    { value: 'all', label: 'All Prices' },
    { value: 'budget', label: '$ Budget' },
    { value: 'moderate', label: '$$ Moderate' },
    { value: 'premium', label: '$$$ Premium' }
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query?.length > 2) {
      onSearch(query, selectedFilters);
    }
  };

  const handleFilterToggle = (filterType, value) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev };
      if (filterType === 'priceRange') {
        newFilters.priceRange = value;
      } else {
        const currentValues = newFilters?.[filterType];
        if (currentValues?.includes(value)) {
          newFilters[filterType] = currentValues?.filter(v => v !== value);
        } else {
          newFilters[filterType] = [...currentValues, value];
        }
      }
      onFilterChange(newFilters);
      return newFilters;
    });
  };

  const clearFilters = () => {
    setSelectedFilters({
      cuisine: [],
      dietary: [],
      priceRange: 'all'
    });
    onFilterChange({
      cuisine: [],
      dietary: [],
      priceRange: 'all'
    });
  };

  const activeFilterCount = 
    selectedFilters?.cuisine?.length + 
    selectedFilters?.dietary?.length + 
    (selectedFilters?.priceRange !== 'all' ? 1 : 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsExpanded(false);
        setShowFilters(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <div className="relative w-full max-w-2xl" ref={searchRef}>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="Search" size={20} color="var(--color-muted-foreground)" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e?.target?.value)}
          onFocus={() => setIsExpanded(true)}
          placeholder="Search for cuisines, dishes, or kitchens..."
          className="w-full h-12 pl-12 pr-24 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                onSearch('', selectedFilters);
              }}
              className="p-1.5 hover:bg-muted rounded-md transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-smooth ${
              showFilters || activeFilterCount > 0
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            <Icon name="SlidersHorizontal" size={16} />
            {activeFilterCount > 0 && (
              <span className="text-xs font-bold">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover rounded-lg shadow-warm-lg z-dropdown max-h-96 overflow-hidden">
          {showFilters ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-lg">Filters</h3>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary hover:underline transition-smooth"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-caption font-semibold text-sm mb-3">Cuisine Type</h4>
                  <div className="flex flex-wrap gap-2">
                    {cuisineOptions?.map((cuisine) => (
                      <button
                        key={cuisine}
                        onClick={() => handleFilterToggle('cuisine', cuisine)}
                        className={`px-4 py-2 rounded-lg text-sm transition-smooth ${
                          selectedFilters?.cuisine?.includes(cuisine)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {cuisine}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-caption font-semibold text-sm mb-3">Dietary Preferences</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {dietaryOptions?.map((option) => (
                      <button
                        key={option?.value}
                        onClick={() => handleFilterToggle('dietary', option?.value)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-smooth ${
                          selectedFilters?.dietary?.includes(option?.value)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        <Icon name={option?.icon} size={18} />
                        <span className="text-sm font-medium">{option?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-caption font-semibold text-sm mb-3">Price Range</h4>
                  <div className="flex gap-2">
                    {priceRanges?.map((range) => (
                      <button
                        key={range?.value}
                        onClick={() => handleFilterToggle('priceRange', range?.value)}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm transition-smooth ${
                          selectedFilters?.priceRange === range?.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {range?.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-96">
              {searchQuery?.length > 0 && searchResults?.length > 0 ? (
                <div className="p-2">
                  <p className="px-4 py-2 text-xs font-caption text-muted-foreground">
                    Search Results
                  </p>
                  {searchResults?.map((result) => (
                    <button
                      key={result?.id}
                      onClick={() => result?.onClick()}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted rounded-lg transition-smooth"
                    >
                      <Image
                        src={result?.image}
                        alt={result?.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 text-left">
                        <p className="font-medium text-sm">{result?.name}</p>
                        <p className="text-xs text-muted-foreground">{result?.category}</p>
                      </div>
                      <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  {recentSearches?.length > 0 && (
                    <div className="p-2">
                      <p className="px-4 py-2 text-xs font-caption text-muted-foreground">
                        Recent Searches
                      </p>
                      {recentSearches?.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearch(search)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted rounded-lg transition-smooth"
                        >
                          <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                          <span className="text-sm">{search}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {popularCuisines?.length > 0 && (
                    <div className="p-2 border-t border-border">
                      <p className="px-4 py-2 text-xs font-caption text-muted-foreground">
                        Popular Cuisines
                      </p>
                      <div className="grid grid-cols-2 gap-2 px-2">
                        {popularCuisines?.map((cuisine) => (
                          <button
                            key={cuisine?.name}
                            onClick={() => handleFilterToggle('cuisine', cuisine?.name)}
                            className="flex items-center gap-2 p-3 bg-muted hover:bg-muted/80 rounded-lg transition-smooth"
                          >
                            <span className="text-2xl">{cuisine?.emoji}</span>
                            <span className="text-sm font-medium">{cuisine?.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchIntegration;